import { useRef, type CSSProperties } from 'react';
import type { Ctx } from '@milkdown/kit/ctx';
import type { useNodeViewFactory } from '@prosemirror-adapter/react';
import { useNodeViewContext } from '@prosemirror-adapter/react';
import { prosePluginsCtx } from '@milkdown/kit/core';
import { Plugin } from '@milkdown/kit/prose/state';
import clsx from 'clsx';

import { ResizeHandle } from '../../components/ResizeHandle';
import { useResizeHandle } from '../../hooks/useResizeHandle';
import { Frame } from './components/Frame';
import './providers'; // Auto-register all providers
import { embedRegistry } from './registry';
import type { EmbedType } from './types';

/** Default embed dimensions */
const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 360;

/**
 * React component for rendering embed blocks in the editor
 */
export const EmbedNodeView = () => {
  const { node, setAttrs, selected, view } = useNodeViewContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Support both 'src' (new schema) and 'href' (ydoc/collab legacy)
  const src = (node.attrs.src || node.attrs.href) as string;
  const width = (node.attrs.width as number) || DEFAULT_WIDTH;
  const height = (node.attrs.height as number) || DEFAULT_HEIGHT;
  const readonly = !view.editable;

  // Resize functionality (independent width/height for embeds like Typeform)
  const {
    width: currentWidth,
    height: currentHeight,
    isDragging,
    handleResizeStart,
  } = useResizeHandle(containerRef, {
    initialWidth: width,
    initialHeight: height,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    maintainAspectRatio: false,
    disabled: readonly,
    onResize: (newWidth, newHeight) => {
      if (!readonly) {
        setAttrs({ width: newWidth, height: newHeight });
      }
    },
    // Clear stored dimensions on reset (don't persist defaults)
    onReset: () => {
      if (!readonly) {
        setAttrs({ width: null, height: null });
      }
    },
  });

  if (!src) {
    console.warn('[EmbedNodeView] No src or href. Full attrs:', node.attrs);

    return <div className="embed-error">Invalid embed URL</div>;
  }

  // Detect embed type from URL if not stored (collab docs may not have it)
  const embedType = (node.attrs.embedType || embedRegistry.detectType(src)) as EmbedType;

  // Get provider and render
  if (embedType) {
    const provider = embedRegistry.get(embedType);
    if (provider) {
      // Validate URL matches expected pattern before rendering.
      // Skip for iframe: `customIframe=true` is only for auto-detecting bare links;
      // explicit `::iframe{src=...}` (and stored embedType iframe) is an author allow.
      if (embedType !== 'iframe' && !provider.matcher(src)) {
        return <div className="embed-error">{provider.invalidUrlMessage}</div>;
      }

      const embedUrl = provider.convertUrl(src);
      const title = provider.renderProps?.title?.(src) || `${provider.label} Embed`;
      const referrerPolicy = provider.renderProps?.referrerPolicy;
      const providerHeight = provider.renderProps?.height?.(src);
      const allow = provider.renderProps?.allow;

      // Priority: 1) user-set height (stored in attrs), 2) provider height, 3) default
      const hasUserSetHeight = node.attrs.height != null;
      const effectiveHeight = hasUserSetHeight ? currentHeight : (providerHeight ?? DEFAULT_HEIGHT);

      const containerStyle: CSSProperties = {
        width: currentWidth || 'auto',
        height: effectiveHeight || 'auto',
        maxWidth: '100%',
        pointerEvents: isDragging ? 'none' : 'all',
      };

      return (
        <div contentEditable={false} className="embed-viewer my-4">
          <div
            ref={containerRef}
            className={clsx(
              'relative mx-auto rounded-lg select-none overflow-hidden group',
              isDragging ? 'transition-none' : 'transition-[width,height] duration-150 ease-in-out',
              selected && 'ring-2 ring-blue-500',
            )}
            style={containerStyle}
          >
            <Frame
              src={embedUrl}
              title={title}
              width="100%"
              height="100%"
              allow={allow}
              referrerPolicy={referrerPolicy}
            />

            {!readonly && (
              <>
                <ResizeHandle side="left" onPointerDown={handleResizeStart('left')} />
                <ResizeHandle side="right" onPointerDown={handleResizeStart('right')} />
                <ResizeHandle side="bottom" onPointerDown={handleResizeStart('bottom')} />
              </>
            )}
          </div>
        </div>
      );
    }
  }

  // Fallback for unknown embed types
  return (
    <div className="embed-fallback">
      <a href={src} target="_blank" rel="noopener noreferrer">
        {src}
      </a>
    </div>
  );
};

/**
 * Configure the embed NodeView with the editor
 */
export function configureEmbedNodeView(ctx: Ctx, nodeViewFactory: ReturnType<typeof useNodeViewFactory>) {
  ctx.update(prosePluginsCtx, (plugins) => [
    ...plugins,
    new Plugin({
      props: {
        nodeViews: {
          embed: nodeViewFactory({ component: EmbedNodeView }),
        },
      },
    }),
  ]);
}
